import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Circle as CircleIcon,
  CloudUpload,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { bloombergApi } from '../services/api';
import { formatDate, formatNumber } from '../utils/formatters';
import { useApp } from '../App';

const Bloomberg = () => {
  const { showNotification } = useApp();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [fxRates, setFxRates] = useState({});
  const [status, setStatus] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchData();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, fxRes, statusRes, historyRes] = await Promise.all([
        bloombergApi.getQuotes(),
        bloombergApi.getFxRates(),
        bloombergApi.getStatus(),
        bloombergApi.getImportHistory(),
      ]);

      setQuotes(quotesRes.data.quotes || []);
      setFxRates(fxRes.data.rates || {});
      setStatus(statusRes.data);
      setImportHistory(historyRes.data || []);
    } catch (error) {
      showNotification('Ошибка загрузки данных Bloomberg', 'error');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    try {
      const wsBase = window.__BCAPITAL_BASE__
        ? window.__BCAPITAL_BASE__.replace(/\/+$/, '')
        : '';
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}${wsBase}/ws/quotes`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'quotes') {
          setQuotes(data.data);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  };

  const handleUpdateCbrRates = async () => {
    try {
      await bloombergApi.getCbrRates();
      showNotification('Курсы ЦБ РФ обновлены', 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка обновления курсов', 'error');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'quotes');

    try {
      const result = await bloombergApi.importFile(formData);
      showNotification(`Импортировано ${result.data.recordsProcessed} записей`, 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка импорта файла', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Bloomberg интеграция
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircleIcon
              sx={{ fontSize: 12, color: wsConnected ? 'success.main' : 'error.main' }}
            />
            <Typography variant="body2" color="text.secondary">
              WebSocket: {wsConnected ? 'Подключен' : 'Отключен'}
            </Typography>
            <CircleIcon
              sx={{ fontSize: 12, color: status?.mode === 'mock' ? 'warning.main' : 'success.main' }}
            />
            <Typography variant="body2" color="text.secondary">
              Режим: {status?.mode === 'mock' ? 'Mock' : 'Live'}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchData}>
          Обновить
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Котировки" />
        <Tab label="Курсы валют" />
        <Tab label="Загрузка данных" />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Quotes Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Котировки в реальном времени
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Тикер</TableCell>
                        <TableCell align="right">Цена</TableCell>
                        <TableCell align="right">Изменение</TableCell>
                        <TableCell align="right">Изменение %</TableCell>
                        <TableCell align="right">Объем</TableCell>
                        <TableCell>Время</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quotes.map((quote) => (
                        <TableRow
                          key={quote.ticker}
                          hover
                          onClick={() => setSelectedTicker(quote.ticker)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Typography fontWeight={600}>{quote.ticker}</Typography>
                          </TableCell>
                          <TableCell align="right">{quote.price?.toFixed(2)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: quote.change >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {quote.change >= 0 ? '+' : ''}{quote.change?.toFixed(2)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: quote.changePercent >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {quote.changePercent >= 0 ? '+' : ''}{quote.changePercent?.toFixed(2)}%
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(quote.volume)}
                          </TableCell>
                          <TableCell>
                            {formatDate(quote.timestamp, 'HH:mm:ss')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedTicker ? `График ${selectedTicker}` : 'Выберите тикер'}
                </Typography>
                {selectedTicker && (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      data={Array.from({ length: 20 }, (_, i) => ({
                        time: i,
                        price: quotes.find((q) => q.ticker === selectedTicker)?.price *
                          (1 + (Math.random() - 0.5) * 0.02),
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis domain={['auto', 'auto']} />
                      <Tooltip />
                      <Line type="monotone" dataKey="price" stroke="#1E3A5F" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* FX Rates Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Курсы валют к USD</Typography>
                  <Button size="small" onClick={handleUpdateCbrRates}>
                    Обновить ЦБ РФ
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Валюта</TableCell>
                        <TableCell align="right">Курс</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(fxRates).map(([currency, rate]) => (
                        <TableRow key={currency}>
                          <TableCell>{currency}</TableCell>
                          <TableCell align="right">{rate.toFixed(4)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Import Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Загрузка файла
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Перетащите файл сюда или нажмите для выбора
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Поддерживаемые форматы: CSV, Excel
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  История импортов
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Файл</TableCell>
                        <TableCell align="right">Записей</TableCell>
                        <TableCell>Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importHistory.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{formatDate(item.date, 'dd.MM.yy HH:mm')}</TableCell>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {item.filename}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.recordsProcessed}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              size="small"
                              color={item.status === 'completed' ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Bloomberg;
